import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { Send, Mail, Building2, User, Paperclip, AlertTriangle, Loader2 } from 'lucide-react';

const ForwardsTab = ({ filter }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/forwards'); setList(data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = (filter || '').toLowerCase();
  const filtered = list.filter((f) =>
    !q || (f.student_name || '').toLowerCase().includes(q) ||
    (f.to_email || '').toLowerCase().includes(q) ||
    (f.college_name || '').toLowerCase().includes(q)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#4b5e4d]">
          <span className="font-semibold text-[#0B5E2A]">{filtered.length}</span> forwarded email{filtered.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[#6b7e6d]"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Send className="h-10 w-10 mx-auto brand-green opacity-60" />
            <div className="mt-3 font-display text-lg text-[#0B5E2A]">No forwards yet</div>
            <p className="mt-1 text-sm text-[#6b7e6d]">When you forward a student to a college, the action will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f0f6ee] text-[#0B5E2A]">
                <tr>
                  <Th>Student</Th><Th>College</Th><Th>Recipient</Th><Th>Attachments</Th><Th>Sent By</Th><Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr key={f.id} className="border-t border-[#0B5E2A]/10 hover:bg-[#faf8ee]">
                    <Td>
                      <div className="flex items-center gap-1.5 font-semibold text-[#0B5E2A]"><User className="h-3.5 w-3.5" />{f.student_name}</div>
                      <div className="text-xs text-[#6b7e6d]">{f.student_email || '—'}</div>
                    </Td>
                    <Td>
                      {f.college_name ? (
                        <div className="inline-flex items-center gap-1.5 text-[#1b2a1f]"><Building2 className="h-3.5 w-3.5 brand-green" />{f.college_name}</div>
                      ) : (<span className="text-xs text-[#6b7e6d] italic">No college (direct)</span>)}
                    </Td>
                    <Td>
                      <a href={`mailto:${f.to_email}`} className="inline-flex items-center gap-1.5 text-[#1e40af] hover:underline">
                        <Mail className="h-3.5 w-3.5" /> {f.to_email}
                      </a>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 bg-[#E1F3E4] text-[#0B5E2A] px-2.5 py-1 rounded-full text-xs font-bold">
                        <Paperclip className="h-3 w-3" /> {f.attached_count || 0}
                      </span>
                      {(f.skipped || []).length > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1.5 bg-[#FEF4DB] text-[#8a5e00] px-2 py-0.5 rounded text-[11px] font-semibold" title={(f.skipped || []).map((s) => s.name).join(', ')}>
                          <AlertTriangle className="h-3 w-3" /> {f.skipped.length} skipped
                        </span>
                      )}
                    </Td>
                    <Td className="text-xs">{f.forwarded_by}</Td>
                    <Td className="text-xs text-[#6b7e6d]">{new Date(f.forwarded_at).toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

export default ForwardsTab;
